import React, { useRef, useState } from 'react';
import { Clipboard, Download, Table2, ZoomOut } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartConfiguration,
  LineController,
  BarController,
  DoughnutController,
  RadarController,
  ScatterController,
  BubbleController
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';

// Register Chart.js components and controllers
ChartJS.register(
  // Scales
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  // Elements
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  // Controllers (required for each chart type)
  LineController,
  BarController,
  DoughnutController,
  RadarController,
  ScatterController,
  BubbleController,
  // Plugins
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

interface InteractiveChartProps {
  config: ChartConfiguration;
  title: string;
  chartType: 'line' | 'bar' | 'pie' | 'donut' | 'area' | 'scatter' | 'radar' | 'bubble';
  width?: number;
  height?: number;
  onExport?: (base64Image: string) => void;
}

type ExportFormat = 'png' | 'jpeg';
type ExportResolution = 1 | 2 | 4;

const InteractiveChart: React.FC<InteractiveChartProps> = ({
  config,
  title,
  chartType,
  width = 800,
  height = 500,
  onExport
}) => {
  const chartRef = useRef<ChartJS>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showDataTable, setShowDataTable] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Enable zoom for appropriate chart types
  const supportsZoom = ['line', 'bar', 'area', 'scatter', 'bubble'].includes(chartType);

  // Check browser support for advanced features
  const supportsFullscreen = typeof document.exitFullscreen !== 'undefined';
  const supportsClipboard = typeof navigator.clipboard !== 'undefined' && typeof ClipboardItem !== 'undefined';

  // Handle fullscreen change events (when user exits with ESC)
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Close export menu when clicking outside
  React.useEffect(() => {
    if (!showExportMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside export menu and export button
      if (target && !target.closest('[data-export-menu]') && !target.closest('[data-export-button]')) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showExportMenu]);

  // Enhanced config with zoom if supported
  const enhancedConfig: ChartConfiguration = {
    ...config,
    options: {
      ...config.options,
      responsive: true,
      maintainAspectRatio: false,
      ...(supportsZoom && {
        plugins: {
          ...(config.options as any)?.plugins,
          zoom: {
            zoom: {
              wheel: {
                enabled: true,
              },
              pinch: {
                enabled: true
              },
              mode: 'xy',
            },
            pan: {
              enabled: true,
              mode: 'xy',
            },
          },
        },
      }),
    } as any,
  };

  const handleExport = async (format: ExportFormat = 'png', resolution: ExportResolution = 1) => {
    if (!chartRef.current) return;

    setIsExporting(true);
    try {
      const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
      const quality = format === 'jpeg' ? 0.95 : undefined;

      let base64Image: string;

      if (resolution === 1) {
        // Standard resolution
        base64Image = chartRef.current.toBase64Image(mimeType, quality);
      } else {
        // High resolution export
        const originalCanvas = chartRef.current.canvas;
        const scaledWidth = originalCanvas.width * resolution;
        const scaledHeight = originalCanvas.height * resolution;

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = scaledWidth;
        tempCanvas.height = scaledHeight;
        const ctx = tempCanvas.getContext('2d');

        if (ctx) {
          ctx.scale(resolution, resolution);
          ctx.drawImage(originalCanvas, 0, 0);
          base64Image = tempCanvas.toDataURL(mimeType, quality);
        } else {
          base64Image = chartRef.current.toBase64Image(mimeType, quality);
        }
      }

      // Download the image
      const link = document.createElement('a');
      const resLabel = resolution > 1 ? `_${resolution}x` : '';
      link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}${resLabel}_chart.${format}`;
      link.href = base64Image;
      link.click();

      // Call callback if provided
      if (onExport) {
        onExport(base64Image);
      }

      // Show success feedback
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
      setShowExportMenu(false);
    } catch (error) {
      console.error('[InteractiveChart] Error exporting chart:', error);
      alert('Failed to export chart. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const copyToClipboard = async () => {
    if (!chartRef.current) return;

    if (!supportsClipboard) {
      alert('Clipboard API is not supported in your browser. Please use the Export button to download the chart.');
      return;
    }

    try {
      const base64Image = chartRef.current.toBase64Image();
      const blob = await (await fetch(base64Image)).blob();

      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);

      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (error) {
      console.error('[InteractiveChart] Error copying to clipboard:', error);
      alert('Failed to copy chart to clipboard. Your browser may not support this feature or permission was denied.');
    }
  };

  const resetZoom = () => {
    if (chartRef.current && supportsZoom) {
      chartRef.current.resetZoom();
    }
  };

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;

    if (!supportsFullscreen) {
      alert('Fullscreen API is not supported in your browser.');
      return;
    }

    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('[InteractiveChart] Error toggling fullscreen:', error);
      alert('Failed to toggle fullscreen mode. Your browser may not support this feature.');
    }
  };

  const exportToCSV = () => {
    try {
      const data = config.data as any;

      // Helper function to escape CSV values
      const escapeCSV = (value: any): string => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      let csv = '';

      if (data.labels) {
        // Standard labeled data (line, bar, etc.)
        csv += 'Label,' + data.datasets.map((d: any) => escapeCSV(d.label || 'Data')).join(',') + '\n';

        data.labels.forEach((label: string, index: number) => {
          const row = [escapeCSV(label)];
          data.datasets.forEach((dataset: any) => {
            const value = dataset.data?.[index];
            row.push(escapeCSV(value));
          });
          csv += row.join(',') + '\n';
        });
      } else if (data.datasets) {
        // Scatter/bubble data
        data.datasets.forEach((dataset: any, dsIndex: number) => {
          if (dsIndex > 0) csv += '\n'; // Separator between datasets
          csv += escapeCSV(dataset.label || 'Data') + '\n';

          if (chartType === 'bubble') {
            csv += 'X,Y,Size\n';
            if (Array.isArray(dataset.data)) {
              dataset.data.forEach((point: any) => {
                const x = point?.x ?? '';
                const y = point?.y ?? '';
                const r = point?.r ?? '';
                csv += `${escapeCSV(x)},${escapeCSV(y)},${escapeCSV(r)}\n`;
              });
            }
          } else {
            csv += 'X,Y\n';
            if (Array.isArray(dataset.data)) {
              dataset.data.forEach((point: any) => {
                const x = point?.x ?? '';
                const y = point?.y ?? '';
                csv += `${escapeCSV(x)},${escapeCSV(y)}\n`;
              });
            }
          }
        });
      }

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_data.csv`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[InteractiveChart] Error exporting CSV:', error);
      alert('Failed to export data as CSV. Please try again.');
    }
  };

  const getChartTypeLabel = () => {
    switch (chartType) {
      case 'donut': return 'Donut Chart';
      case 'scatter': return 'Scatter Plot';
      case 'radar': return 'Radar Chart';
      case 'bubble': return 'Bubble Chart';
      default: return chartType.charAt(0).toUpperCase() + chartType.slice(1) + ' Chart';
    }
  };

  const renderDataTable = () => {
    const data = config.data as any;

    if (!data || !data.datasets || !Array.isArray(data.datasets)) return null;

    if (data.labels && Array.isArray(data.labels)) {
      // Standard labeled data
      return (
        <table style={styles.dataTable}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>Label</th>
              {data.datasets.map((dataset: any, idx: number) => (
                <th key={idx} style={styles.tableHeader}>{dataset?.label || `Series ${idx + 1}`}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.labels.map((label: string, rowIdx: number) => (
              <tr key={rowIdx} style={rowIdx % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd}>
                <td style={styles.tableCell}>{label ?? '-'}</td>
                {data.datasets.map((dataset: any, colIdx: number) => {
                  const value = dataset?.data?.[rowIdx];
                  return (
                    <td key={colIdx} style={styles.tableCell}>
                      {typeof value === 'number' ? value.toLocaleString() : (value ?? '-')}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else {
      // Scatter/bubble data
      return (
        <div>
          {data.datasets.map((dataset: any, idx: number) => {
            if (!dataset || !Array.isArray(dataset.data)) return null;

            return (
              <div key={idx} style={{ marginBottom: '1.5rem' }}>
                <h4 style={styles.dataTableTitle}>{dataset.label || `Series ${idx + 1}`}</h4>
                <table style={styles.dataTable}>
                  <thead>
                    <tr>
                      <th style={styles.tableHeader}>X</th>
                      <th style={styles.tableHeader}>Y</th>
                      {chartType === 'bubble' && <th style={styles.tableHeader}>Size</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {dataset.data.map((point: any, pointIdx: number) => (
                      <tr key={pointIdx} style={pointIdx % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd}>
                        <td style={styles.tableCell}>
                          {typeof point?.x === 'number' ? point.x.toLocaleString() : (point?.x ?? '-')}
                        </td>
                        <td style={styles.tableCell}>
                          {typeof point?.y === 'number' ? point.y.toLocaleString() : (point?.y ?? '-')}
                        </td>
                        {chartType === 'bubble' && (
                          <td style={styles.tableCell}>
                            {typeof point?.r === 'number' ? point.r.toLocaleString() : (point?.r ?? '-')}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      );
    }
  };

  // Validate required props (after all hooks)
  if (!config || !config.type || !config.data) {
    console.error('[InteractiveChart] Invalid chart configuration:', { config, title, chartType });
    return (
      <div style={{
        padding: '1rem',
        backgroundColor: 'var(--danger-soft)',
        borderRadius: '8px',
        border: '1px solid var(--cg-terra-200)',
        marginTop: '1rem',
        marginBottom: '1rem'
      }}>
        <strong>⚠️ Chart Error</strong>
        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: 'var(--fg-3)' }}>
          Invalid chart configuration. Missing required properties.
        </p>
      </div>
    );
  }

  if (!title || !chartType) {
    console.error('[InteractiveChart] Missing title or chartType:', { title, chartType });
    return (
      <div style={{
        padding: '1rem',
        backgroundColor: 'var(--danger-soft)',
        borderRadius: '8px',
        border: '1px solid var(--cg-terra-200)',
        marginTop: '1rem',
        marginBottom: '1rem'
      }}>
        <strong>⚠️ Chart Error</strong>
        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: 'var(--fg-3)' }}>
          Missing chart title or type.
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{...styles.container, ...(isFullscreen ? styles.fullscreenContainer : {})}}>
      <div style={styles.chartWrapper}>
        <Chart
          ref={chartRef}
          type={config.type as any}
          data={config.data as any}
          options={enhancedConfig.options as any}
        />
      </div>

      <div style={styles.toolbar}>
        <div style={styles.toolbarLeft}>
          <button
            data-export-button
            onClick={() => setShowExportMenu(!showExportMenu)}
            disabled={isExporting}
            style={{
              ...styles.toolbarButton,
              ...(isExporting ? styles.toolbarButtonDisabled : {})
            }}
            title="Export options"
          >
            {isExporting ? (
              <>
                <span style={styles.buttonSpinner}></span>
                Exporting...
              </>
            ) : showCopied ? (
              <>✓ Downloaded!</>
            ) : (
              <>📥 Export</>
            )}
          </button>

          {showExportMenu && (
            <div data-export-menu style={styles.exportMenu}>
              <div style={styles.exportMenuSection}>
                <div style={styles.exportMenuLabel}>Quick Export:</div>
                <button
                  onClick={() => handleExport('png', 1)}
                  style={styles.exportMenuItem}
                >
                  PNG (Standard)
                </button>
                <button
                  onClick={() => handleExport('png', 2)}
                  style={styles.exportMenuItem}
                >
                  PNG (2x Retina)
                </button>
                <button
                  onClick={() => handleExport('jpeg', 1)}
                  style={styles.exportMenuItem}
                >
                  JPEG
                </button>
              </div>
              <div style={styles.exportMenuDivider} />
              {supportsClipboard && (
                <button
                  onClick={copyToClipboard}
                  style={styles.exportMenuItem}
                >
                  <Clipboard size={14} strokeWidth={1.5} style={{ verticalAlign: '-2px', marginRight: 6 }} /> Copy to Clipboard
                </button>
              )}
              <button
                onClick={exportToCSV}
                style={styles.exportMenuItem}
              >
                <Download size={14} strokeWidth={1.5} style={{ verticalAlign: '-2px', marginRight: 6 }} /> Export Data (CSV)
              </button>
            </div>
          )}

          <button
            onClick={() => setShowDataTable(!showDataTable)}
            style={styles.toolbarButton}
            title="Toggle data table"
          >
            <Table2 size={14} strokeWidth={1.5} style={{ verticalAlign: '-2px', marginRight: 6 }} />
            {showDataTable ? 'Hide Data' : 'Show Data'}
          </button>

          {supportsZoom && (
            <button
              onClick={resetZoom}
              style={styles.toolbarButton}
              title="Reset zoom"
            >
              <ZoomOut size={14} strokeWidth={1.5} style={{ verticalAlign: '-2px', marginRight: 6 }} /> Reset Zoom
            </button>
          )}

          {supportsFullscreen && (
            <button
              onClick={toggleFullscreen}
              style={styles.toolbarButton}
              title="Toggle fullscreen"
            >
              {isFullscreen ? '⛶ Exit Fullscreen' : '⛶ Fullscreen'}
            </button>
          )}
        </div>

        <span style={styles.chartInfo}>
          {getChartTypeLabel()}
          {' • '}
          Interactive
          {supportsZoom && ' • Zoom/Pan Enabled'}
        </span>
      </div>

      {showDataTable && (
        <div style={styles.dataTableContainer}>
          <div style={styles.dataTableHeader}>
            <h3 style={styles.dataTableTitleMain}>Chart Data</h3>
          </div>
          {renderDataTable()}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    marginTop: '1.5rem',
    marginBottom: '1.5rem',
    borderRadius: '12px',
    border: '1px solid var(--border-subtle)',
    backgroundColor: 'var(--bg-surface-1)',
    padding: '1.5rem',
    boxShadow: 'var(--shadow-sm)',
    position: 'relative' as const,
  },
  fullscreenContainer: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 'var(--z-modal)' as unknown as number,
    margin: 0,
    borderRadius: 0,
    maxWidth: '100%',
    height: '100vh',
  },
  chartWrapper: {
    position: 'relative' as const,
    width: '100%',
    height: '500px',
    marginBottom: '1rem',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: '1rem',
    borderTop: '1px solid var(--border-subtle)',
    position: 'relative' as const,
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    position: 'relative' as const,
  },
  toolbarButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.5rem 1rem',
    backgroundColor: 'var(--accent)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'var(--font-sans)',
    boxShadow: 'var(--shadow-md)',
    whiteSpace: 'nowrap' as const,
  },
  toolbarButtonDisabled: {
    backgroundColor: 'var(--bg-surface-1)',
    color: 'var(--fg-3)',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  buttonSpinner: {
    width: '12px',
    height: '12px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid #FFFFFF',
    borderRadius: '50%',
    animation: 'cgConicSpin 0.6s linear infinite',
    display: 'inline-block',
  },
  exportMenu: {
    position: 'absolute' as const,
    top: '100%',
    left: 0,
    marginTop: '0.5rem',
    backgroundColor: '#FFFFFF',
    border: '1px solid var(--border-1)',
    borderRadius: '8px',
    boxShadow: 'var(--shadow-lg)',
    padding: '0.5rem',
    minWidth: '200px',
    zIndex: 1000,
  },
  exportMenuSection: {
    padding: '0.5rem 0',
  },
  exportMenuLabel: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: 'var(--fg-3)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    padding: '0 0.5rem 0.25rem',
  },
  exportMenuItem: {
    width: '100%',
    padding: '0.625rem 0.75rem',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '6px',
    textAlign: 'left' as const,
    fontSize: '0.875rem',
    color: 'var(--fg-1)',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    fontFamily: 'var(--font-sans)',
    display: 'block',
  },
  exportMenuDivider: {
    height: '1px',
    backgroundColor: 'var(--border-subtle)',
    margin: '0.5rem 0',
  },
  chartInfo: {
    fontSize: '0.8125rem',
    color: 'var(--fg-3)',
    fontWeight: 400,
  },
  dataTableContainer: {
    marginTop: '1.5rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid var(--border-subtle)',
  },
  dataTableHeader: {
    marginBottom: '1rem',
  },
  dataTableTitleMain: {
    fontSize: '1rem',
    fontWeight: 500,
    color: 'var(--fg-1)',
    margin: 0,
  },
  dataTableTitle: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--fg-2)',
    marginBottom: '0.5rem',
  },
  dataTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.8125rem',
  },
  tableHeader: {
    padding: '0.625rem 0.75rem',
    backgroundColor: 'rgba(184, 197, 214, 0.15)',
    borderBottom: '2px solid var(--border-1)',
    textAlign: 'left' as const,
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  tableCell: {
    padding: '0.5rem 0.75rem',
    borderBottom: '1px solid var(--border-subtle)',
    color: 'var(--fg-2)',
  },
  tableRowEven: {
    backgroundColor: 'transparent',
  },
  tableRowOdd: {
    backgroundColor: 'rgba(184, 197, 214, 0.05)',
  },
};

export default InteractiveChart;
